import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bot, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface AgentPermissions {
  auto_organize_data: boolean;
  auto_create_views: boolean;
  auto_suggest_automations: boolean;
  proactive_notifications: boolean;
  daily_summary: boolean;
}

export function AgentSettings() {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<AgentPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const fetchPermissions = async () => {
      const { data, error } = await supabase
        .from("agent_permissions")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Failed to fetch agent permissions:", error);
      }

      if (data) {
        setPermissions(data);
      } else {
        // Create default permissions
        const defaults: AgentPermissions = {
          auto_organize_data: false,
          auto_create_views: false,
          auto_suggest_automations: true,
          proactive_notifications: true,
          daily_summary: false,
        };
        
        await supabase.from("agent_permissions").insert({
          user_id: user.id,
          ...defaults,
        });
        
        setPermissions(defaults);
      }

      setLoading(false);
    };

    fetchPermissions();
  }, [user]);

  const updatePermission = async (key: keyof AgentPermissions, value: boolean) => {
    if (!user || !permissions) return;

    setSaving(key);
    
    const { error } = await supabase
      .from("agent_permissions")
      .update({ [key]: value })
      .eq("user_id", user.id);

    if (error) {
      toast.error("Failed to update setting");
      console.error("Failed to update permission:", error);
    } else {
      setPermissions((prev) => prev ? { ...prev, [key]: value } : null);
      toast.success("Setting updated");
    }

    setSaving(null);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const settingsConfig = [
    {
      key: "proactive_notifications" as const,
      label: "Proactive Notifications",
      description: "Get notified about important events, reminders, and insights",
    },
    {
      key: "auto_suggest_automations" as const,
      label: "Automation Suggestions",
      description: "Receive AI suggestions for automations based on your patterns",
    },
    {
      key: "auto_organize_data" as const,
      label: "Auto-Organize Data",
      description: "Allow the agent to suggest reorganizing messy data",
    },
    {
      key: "auto_create_views" as const,
      label: "Auto-Create Views",
      description: "Let the agent create views based on your filtering patterns",
    },
    {
      key: "daily_summary" as const,
      label: "Daily Summary",
      description: "Receive a daily summary of your data activity",
    },
  ];

  return (
    <Card>
      <CardHeader className="p-4 md:p-6">
        <CardTitle className="flex items-center gap-2 text-lg md:text-xl">
          <Bot className="h-4 w-4 md:h-5 md:w-5" />
          AI Agent
        </CardTitle>
        <CardDescription className="text-sm">
          Configure how the AI agent helps you
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-0 md:p-6 md:pt-0 space-y-4">
        {settingsConfig.map((setting) => (
          <div key={setting.key} className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor={setting.key} className="text-sm font-medium">
                {setting.label}
              </Label>
              <p className="text-xs text-muted-foreground">
                {setting.description}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {saving === setting.key && (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              )}
              <Switch
                id={setting.key}
                checked={permissions?.[setting.key] ?? false}
                onCheckedChange={(checked) => updatePermission(setting.key, checked)}
                disabled={saving !== null}
              />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
